import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { Badge, Button, CheckSquare, Clock, CurrencyDollar, UserPlus01 } from "@opendate-app/setlist";
import { toastError } from "../../../../shared/toastHelper";
import { fetchGuestList } from "./guest_list/guestListApi";
import GuestListCard from "./guest_list/GuestListCard";

const dayKey = (date, timeZone) =>
  date.toLocaleDateString("en-US", { year: "numeric", month: "2-digit", day: "2-digit", ...(timeZone ? { timeZone } : {}) });

const headline = ({ startTime, venueName, timeZone }) => {
  const venue = venueName ? ` at ${venueName}` : "";
  if (!startTime) return `Show day${venue}`;
  const start = new Date(startTime);
  if (dayKey(start, timeZone) === dayKey(new Date(), timeZone)) return `Tonight${venue}`;
  const date = start.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", ...(timeZone ? { timeZone } : {}) });
  return `${date}${venue}`;
};

const doorsLabel = (doorTime, timeZone) =>
  `Doors ${new Date(doorTime).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", ...(timeZone ? { timeZone } : {}) })}`;

const DayOfShowBar = ({ teamId, confirmId, startTime, doorTime, venueName, timeZone, ticketsSold, capacity, published, guestListAvailable, onSelectTab }) => {
  const [guestList, setGuestList] = useState(null);
  const [guestListFailed, setGuestListFailed] = useState(false);
  const [guestListOpen, setGuestListOpen] = useState(false);
  const latestRequest = useRef(0);

  const loadGuestList = () => {
    const request = latestRequest.current + 1;
    latestRequest.current = request;
    fetchGuestList({ teamId, confirmId })
      .then(({ data }) => {
        if (request !== latestRequest.current) return;
        setGuestList(data);
        setGuestListFailed(false);
      })
      .catch(() => {
        if (request !== latestRequest.current) return;
        setGuestListFailed(true);
        toastError("Couldn’t load the guest list.");
      });
  };

  const replaceGuestList = (data) => {
    latestRequest.current += 1;
    setGuestList(data);
  };

  const toggleGuestList = () => {
    if (!guestListOpen) loadGuestList();
    setGuestListOpen((open) => !open);
  };

  useEffect(() => {
    setGuestList(null);
    setGuestListFailed(false);
    if (!guestListAvailable) return undefined;
    loadGuestList();
    return () => { latestRequest.current += 1; };
  }, [teamId, confirmId, guestListAvailable]);

  const details = [
    doorTime ? doorsLabel(doorTime, timeZone) : null,
    `${guestList ? guestList.tickets_sold : ticketsSold} of ${guestList ? guestList.capacity : capacity} sold`,
  ].filter(Boolean).join(" · ");

  return (
    <>
      <div className="d2-day-of-show">
        <div className="d2-day-of-show-summary">
          <span className="d2-day-of-show-icon"><Clock width={20} height={20} /></span>
          <div>
            <div className="d2-day-of-show-title">{headline({ startTime, venueName, timeZone })}</div>
            <div className="d2-day-of-show-details">{details}</div>
          </div>
        </div>
        <div className="d2-day-of-show-actions">
          {guestListAvailable && (
            <Button
              variant={guestListOpen ? "secondary-color" : "secondary"}
              icon={UserPlus01}
              aria-expanded={guestListOpen}
              disabled={!guestList && !guestListFailed}
              onClick={toggleGuestList}
            >
              Guest list
              {guestList && guestList.guests.length > 0 && (
                <Badge size="sm" color={guestListOpen ? "brand" : "gray"}>{guestList.guests.length}</Badge>
              )}
            </Button>
          )}
          {published && (
            <Button as="a" href={`/teams/${teamId}/confirms/${confirmId}/ticket_audit`} variant="secondary" icon={CheckSquare}>
              Ticket Audit
            </Button>
          )}
          {onSelectTab && (
            <Button variant="primary" icon={CurrencyDollar} onClick={() => onSelectTab("settlements")}>
              Settlement
            </Button>
          )}
        </div>
      </div>
      {guestListOpen && guestList && (
        <GuestListCard teamId={teamId} confirmId={confirmId} guestList={guestList} onGuestListChange={replaceGuestList} />
      )}
    </>
  );
};

DayOfShowBar.propTypes = {
  teamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  confirmId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  startTime: PropTypes.string,
  doorTime: PropTypes.string,
  venueName: PropTypes.string,
  timeZone: PropTypes.string,
  ticketsSold: PropTypes.number,
  capacity: PropTypes.number,
  published: PropTypes.bool,
  guestListAvailable: PropTypes.bool,
  onSelectTab: PropTypes.func,
};

export default DayOfShowBar;
